// Analytics tab › Business Areas (spec §6): master-detail — list of the
// project's Business Areas (A) on the left, the EXISTING embedded
// BusinessAreaDesignerPage on the right. "New" generates a draft area with
// AI from a name + description.
import { useCallback, useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import BusinessAreaDesignerPage from '@/pages/designers/BusinessAreaDesignerPage'
import {
  generateBusinessArea,
  listBusinessAreas,
  saveBusinessArea,
  deleteBusinessArea,
  type BusinessAreaSummary,
} from '@/api/designers'
import ObjectMasterDetail, { type MasterItem } from './ObjectMasterDetail'
import { EmptyStateCoach, TypeChip, errMessage, slugify } from './shared'

export default function BusinessAreasSection({
  project, registerLeaveGuard, initialObjName, openNonce, onChanged,
}: {
  project: string
  registerLeaveGuard?: (fn: ((proceed: () => void) => void) | null) => void
  initialObjName?: string
  openNonce?: number
  onChanged?: () => void
}) {
  const [areas, setAreas] = useState<BusinessAreaSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await listBusinessAreas(project)
      setAreas(res.business_areas || [])
    } catch (err) {
      setError(errMessage(err, 'Could not load business areas'))
    } finally {
      setLoading(false)
    }
  }, [project])

  useEffect(() => { void refresh() }, [refresh])

  const items: MasterItem[] = areas.map((a) => ({
    id: a.area_name,
    name: a.area_name,
    description: a.description || undefined,
    tags: [`${a.table_count} table${a.table_count === 1 ? '' : 's'}`],
    badge: (a.visibility || '').toLowerCase() === 'published'
      ? { text: 'Published', tone: 'success' as const }
      : { text: 'Draft', tone: 'muted' as const },
  }))

  const handleCreate = async (name: string, description: string): Promise<string | null> => {
    // Business Area names are file-safe identifiers (spaces are not allowed by
    // the persistence layer — see get_model_path / save-business-area), so
    // slugify BEFORE sending, exactly like the sibling Schema section does.
    // Passing a raw "Purchase WH" made the generator write "Purchase WH.json"
    // while the read-back looked for "Purchase_WH.json" → empty/blank area.
    const areaName = slugify(name)
    try {
      const res = await generateBusinessArea({
        project,
        area_name: areaName,
        description: description || name,
      })
      if (res.error) {
        toast.error(res.error)
        return null
      }
      toast.success(`Business area "${areaName}" drafted — review and save it`)
      await refresh()
      onChanged?.()
      return areaName
    } catch (err) {
      toast.error(errMessage(err, 'Could not generate the business area'))
      return null
    }
  }

  // Manual path: persist a blank area (no AI) and open the designer so the user
  // picks tables/joins and writes the description themselves.
  const handleCreateManual = async (name: string, description: string): Promise<string | null> => {
    const areaName = slugify(name)
    try {
      const payload = {
        BUSINESS_AREA: {
          AREA_NAME: areaName,
          DESCRIPTION: description || name,
          AREA_TYPE: 'A', CONNECTION_ID: '', SCHEMA_NAME: '', APPLICATION: 'Default',
          MAIN_TABLE: '', JOINS_DESCRIPTION: '',
          TABLES: [], JOINS: [], ROW_SECURITY: [], COLUMN_SECURITY: [],
          AI_INSTRUCTIONS: '', EXAMPLE_QUESTIONS: [], CREATE_TARGETS: [],
          ACCESS: { ALLOW_ROLES: [], DENY_ROLES: [] }, VISIBILITY: 'published',
        },
      }
      await saveBusinessArea(project, areaName, payload)
      toast.success(`Business area "${areaName}" created — add its tables, joins and description`)
      await refresh()
      onChanged?.()
      return areaName
    } catch (err) {
      toast.error(errMessage(err, 'Could not create the business area'))
      return null
    }
  }

  return (
    <ObjectMasterDetail
      project={project}
      typeCode="A"
      heading="Business Areas"
      subCopy="Curated topics the AI Assistant can answer questions on, in plain language. Each area holds its own tables, joins and security."
      items={items}
      loading={loading}
      error={error}
      onRetry={() => void refresh()}
      newLabel="New business area"
      newDialogTitle="New business area"
      namePlaceholder="e.g. Sales"
      descPlaceholder="e.g. Orders, revenue and customer performance"
      createButtonLabel="Generate with AI"
      onCreate={handleCreate}
      onCreateManual={handleCreateManual}
      manualButtonLabel="Create manually"
      onObjectSaved={() => { void refresh(); onChanged?.() }}
      registerLeaveGuard={registerLeaveGuard}
      initialObjName={initialObjName}
      openNonce={openNonce}
      deleteConfig={{
        objType: 'A',
        objLabel: 'business area',
        doDelete: async (id) => { await deleteBusinessArea(project, id) },
        onDeleted: () => { void refresh(); onChanged?.() },
      }}
      emptyState={(openNew) => (
        <EmptyStateCoach
          icon={<TypeChip code="A" size={26} />}
          title="No business areas yet"
          body="A Business Area is a topic the AI Assistant can answer questions on — its tables, joins and security in plain language. Create one and the Assistant (and your visuals) can use it."
          action={
            <button className="btn btn-primary btn-sm" onClick={openNew}>
              + New business area
            </button>
          }
        />
      )}
      renderDesigner={(objName, common) => (
        <BusinessAreaDesignerPage key={`${project}:${objName}`} {...common} />
      )}
    />
  )
}
